import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { SecretInput } from "@/components/design/SecretInput";
import { ModelConfigError, loadModelConfig, saveModelConfig } from "@/lib/model-config";
import { SettingsCard } from "./SettingsCard";
import { KnowledgeDocumentsCard } from "./KnowledgeDocumentsCard";
// Official Mistral model icons (downloaded from mistral.ai/brand, used as-is).
import mistralLarge from "@/assets/providers/mistral/mistral-large.svg";
import mistralSmall from "@/assets/providers/mistral/mistral-small.svg";
import mistralOcr from "@/assets/providers/mistral/mistral-ocr.svg";
import mistralImage from "@/assets/providers/mistral/mistral-image.svg";
import mistralEmbedding from "@/assets/providers/mistral/mistral-embedding.svg";

// AI Model panel (Settings · AI Model). Pick the upstream model provider and supply each provider's
// API key (password-masked). Save POSTs to the BFF (/ai/model-config), which writes the provider +
// non-empty keys into gemstone; model-gateway reads them back (and hot-reloads). Admin-only on the BFF.
// The provider names, API-key env vars and resolved model ids mirror model-gateway config/default.toml.

type Provider = "mistral" | "openai" | "anthropic";

const PROVIDERS = [
  { value: "mistral", label: "Mistral AI" },
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
] as const;

interface ProviderInfo {
  envVar: string;
  keyPlaceholder: string;
  models: { kind: string; id: string }[];
}

const PROVIDER_INFO: Record<Provider, ProviderInfo> = {
  mistral: {
    envVar: "MODEL_GATEWAY_MISTRAL__API_KEY",
    keyPlaceholder: "your Mistral API key",
    models: [
      { kind: "small", id: "mistral-small-latest" },
      { kind: "large", id: "mistral-large-latest" },
      // Pixtral is deprecated — vision is folded into Large 3.
      { kind: "image", id: "mistral-large-latest" },
      { kind: "embedding", id: "mistral-embed" },
      { kind: "ocr", id: "mistral-ocr-latest" },
      { kind: "thinking", id: "magistral-medium-latest" },
    ],
  },
  openai: {
    envVar: "MODEL_GATEWAY_OPENAI__API_KEY",
    keyPlaceholder: "sk-…",
    models: [
      { kind: "small", id: "gpt-5.6-luna" },
      { kind: "large", id: "gpt-5.6-sol" },
      { kind: "image", id: "gpt-5.6-sol" },
      { kind: "embedding", id: "text-embedding-3-small" },
      // No `ocr` row: the gateway defines no ocr kind for openai.
      { kind: "thinking", id: "gpt-5.6-terra" },
    ],
  },
  anthropic: {
    envVar: "MODEL_GATEWAY_ANTHROPIC__API_KEY",
    keyPlaceholder: "sk-ant-…",
    models: [
      { kind: "small", id: "claude-haiku-4-5" },
      { kind: "large", id: "claude-opus-5" },
      { kind: "image", id: "claude-opus-5" },
      // Anthropic has no first-party embedding model; Voyage AI is its recommended partner
      // (not served by the api.anthropic.com endpoint — placeholder for the mock).
      { kind: "embedding", id: "voyage-3-large" },
      // No dedicated OCR endpoint — OCR runs through the vision model (claude-opus-5).
      { kind: "ocr", id: "claude-opus-5" },
      { kind: "thinking", id: "claude-opus-5" },
    ],
  },
};

// Model-kind → official Mistral icon. Only Mistral ships per-model icons; other
// providers fall back to no icon.
const MISTRAL_MODEL_ICONS: Record<string, string> = {
  large: mistralLarge,
  small: mistralSmall,
  ocr: mistralOcr,
  image: mistralImage,
  embedding: mistralEmbedding,
};

// Optional per-provider brand logo, dropped into
// assets/providers/<provider>/logo.svg. Absent → the tile shows the name text.
const PROVIDER_LOGOS = import.meta.glob("../../assets/providers/*/logo.svg", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

function providerLogo(value: string): string | undefined {
  const match = Object.entries(PROVIDER_LOGOS).find(([path]) =>
    path.includes(`/providers/${value}/logo.svg`),
  );
  return match?.[1];
}

export function ModelPanel() {
  const intl = useIntl();
  const [provider, setProvider] = useState<Provider>("mistral");
  // The persisted/active provider (what the backend embeds against). Diverges from `provider` the moment
  // a different tile is selected, before Save — that gap is what the switch hint below keys off.
  const [savedProvider, setSavedProvider] = useState<Provider>("mistral");
  const [keys, setKeys] = useState<Record<Provider, string>>({ mistral: "", openai: "", anthropic: "" });
  const [saved, setSaved] = useState(false);
  // Bumped on a successful save so KnowledgeDocumentsCard reloads and re-derives per-provider availability.
  const [reembedRefresh, setReembedRefresh] = useState(0);
  // After a provider change, uploads are blocked until this timestamp: model-gateway can take ~30s to
  // pick the switch up from gemstone, and the ingestion worker embeds against whatever provider the
  // gateway reports at processing time — so an immediate upload would embed for the OLD provider.
  const [uploadBlockedUntil, setUploadBlockedUntil] = useState<number | null>(null);
  // Holds the localized error message to show in the toast (null = no error).
  const [error, setError] = useState<string | null>(null);

  // Load the persisted state so the active provider + stored keys survive a refresh. A failed load
  // (not admin / gemstone down) just leaves the defaults — the settings page still renders.
  useEffect(() => {
    let cancelled = false;
    loadModelConfig()
      .then((state) => {
        if (cancelled) return;
        if (state.provider) {
          setProvider(state.provider);
          setSavedProvider(state.provider);
        }
        setKeys((k) => ({ ...k, ...state.keys }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Map the save's HTTP status to a specific message so the user knows what to fix: not signed in,
  // not an admin, feature not configured, or the config store failing.
  const saveErrorMessage = (status: number): string => {
    switch (status) {
      case 401:
        return intl.formatMessage({
          id: "model.saveError.unauthorized",
          defaultMessage: "Your session has expired – sign in again.",
        });
      case 403:
        return intl.formatMessage({
          id: "model.saveError.forbidden",
          defaultMessage: "You need admin rights to change the model configuration.",
        });
      case 501:
        return intl.formatMessage({
          id: "model.saveError.notConfigured",
          defaultMessage: "Model configuration isn't enabled on this server.",
        });
      case 502:
        return intl.formatMessage({
          id: "model.saveError.upstream",
          defaultMessage: "Couldn't reach the configuration store – try again.",
        });
      default:
        return intl.formatMessage({
          id: "model.saveError",
          defaultMessage: "Couldn't save model settings – try again.",
        });
    }
  };

  const onSave = async () => {
    setError(null);
    try {
      await saveModelConfig({ provider, keys });
      if (provider !== savedProvider) setUploadBlockedUntil(Date.now() + 30_000);
      setSavedProvider(provider);
      setReembedRefresh((n) => n + 1);
      setSaved(true);
      // Longer than a bare "saved" toast: the message now mentions the ~30s propagation delay, so give
      // the user time to read it.
      window.setTimeout(() => setSaved(false), 5000);
    } catch (e) {
      // ModelConfigError carries the HTTP status; a transport failure (no response) → status 0 → generic.
      const status = e instanceof ModelConfigError ? e.status : 0;
      setError(saveErrorMessage(status));
      window.setTimeout(() => setError(null), 4000);
    }
  };

  // A different tile means a different embedding model; uploaded docs stay embedded for the saved
  // provider until re-embedded (spec §8/§11), so name the change so the admin knows to re-embed.
  const embedModelId = (p: Provider) => PROVIDER_INFO[p].models.find((m) => m.kind === "embedding")?.id ?? "—";
  const switching = provider !== savedProvider;

  return (
    <>
      <SettingsCard
        title={intl.formatMessage({ id: "model.providerTitle", defaultMessage: "Provider" })}
        sub={intl.formatMessage({
          id: "model.providerSub",
          defaultMessage: "The upstream model stack. Model kinds resolve to this provider's concrete models.",
        })}
      >
        {/* Provider as selectable tiles. The active tile carries the accent
            ring, a breathing live dot, a "Connected" line and its API-key
            field, and grows downward to reveal them (top edge fixed via
            items-start). The key input can't sit inside the <button> (no nested
            interactive elements), so the header is the button and the key + env
            var render as siblings below it. */}
        {/* min-height >= the tallest active tile (key + full model stack) so the
            row height never changes when switching provider: the collapsing and
            expanding tiles animate inside a constant-height row, so the Provider
            card doesn't shrink/grow (no jitter). */}
        {/* min-height sized for the tallest active tile at 6 model rows (thinking added). */}
        <div className="grid grid-cols-1 items-start gap-3 @sm:grid-cols-3 sm:min-h-[23rem]">
          {PROVIDERS.map((p) => {
            const on = p.value === provider;
            // "Connected" = this provider has a key (loaded from gemstone or just typed).
            const connected = keys[p.value].trim().length > 0;
            const pInfo = PROVIDER_INFO[p.value];
            return (
              <div
                key={p.value}
                className={`box overflow-hidden transition ${on ? "bg-ink/[0.06] ring-accent" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setProvider(p.value)}
                  aria-pressed={on}
                  className={`flex w-full items-center justify-between gap-2 p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30 ${
                    on ? "" : "hover:bg-ink/[0.06]"
                  }`}
                >
                  <span className="text-sm font-medium">{p.label}</span>
                  {connected ? <span className="h-[7px] w-[7px] rounded-full bg-accent pulse-dot" aria-hidden /> : null}
                </button>
                {/* Connected + API key collapse via grid-rows 0fr→1fr so the tile
                    grows/shrinks smoothly; kept mounted so the open animates. */}
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
                    on ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div
                    className={`overflow-hidden transition-opacity duration-300 motion-reduce:transition-none ${
                      on ? "opacity-100" : "opacity-0 invisible"
                    }`}
                  >
                    <div className="px-4 pb-4">
                      {connected ? (
                        <div className="reveal-rise mb-2 field-label">
                          {intl.formatMessage({ id: "model.connected", defaultMessage: "Connected" })}
                        </div>
                      ) : null}
                      <SecretInput
                        id={`model-key-${p.value}`}
                        value={keys[p.value]}
                        onChange={(v) => setKeys((k) => ({ ...k, [p.value]: v }))}
                        ariaLabel={intl.formatMessage(
                          { id: "model.keyAriaLabel", defaultMessage: "{provider} API key" },
                          { provider: p.label },
                        )}
                        placeholder={pInfo.keyPlaceholder}
                      />
                      <div className="field-meta mt-1 font-mono text-ink/40">{pInfo.envVar}</div>

                      {/* This provider's resolved model stack lives in its own
                          box. Keyed by `on` so the staggered reveal replays each
                          time the box opens. Mistral ships per-model icons;
                          OpenAI/Anthropic reuse their provider logo. Brand marks
                          stay in their brand colors by requirement — an
                          intentional exception to the monochrome palette. */}
                      <div className="field-label mb-2 mt-4">
                        {intl.formatMessage({ id: "model.resolvedModels", defaultMessage: "Resolved models" })}
                      </div>
                      <div key={on ? "open" : "closed"} className="space-y-1.5">
                        {pInfo.models.map((m, i) => {
                          const icon =
                            p.value === "mistral" ? MISTRAL_MODEL_ICONS[m.kind] : providerLogo(p.value);
                          return (
                            <div key={m.kind} className="flex items-center justify-between gap-4">
                              <span className="field-label">{m.kind === "ocr" ? "OCR" : m.kind}</span>
                              <span
                                className="reveal-rise field-value flex items-center gap-2 font-mono text-ink/70"
                                style={{ animationDelay: `${i * 110}ms` }}
                              >
                                {icon ? <img src={icon} alt="" aria-hidden className="h-5 w-5 shrink-0" /> : null}
                                <span className="reveal-wipe" style={{ animationDelay: `${i * 110 + 90}ms` }}>
                                  {m.id}
                                </span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {switching ? (
          <div className="box mt-4 bg-ink/[0.02] p-3 field-meta text-ink/70">
            {intl.formatMessage(
              {
                id: "model.providerSwitchHint",
                defaultMessage:
                  "Switching to {provider} changes the embedding model ({from} → {to}). It may be necessary to reprocess the documents again after saving so they stay searchable.",
              },
              {
                provider: PROVIDERS.find((p) => p.value === provider)?.label ?? provider,
                from: embedModelId(savedProvider),
                to: embedModelId(provider),
              },
            )}
          </div>
        ) : null}

        {/* Save acts on the Provider section only (provider + API keys) — kept inside this card, not after
            the documents card below, so it reads as the provider config's action. */}
        <div className="mt-4 flex justify-end">
          <Button variant="primary" onClick={onSave}>
            {intl.formatMessage({ id: "common.save", defaultMessage: "Save" })}
          </Button>
        </div>
      </SettingsCard>

      {/* The documents section keys off the STORED/active provider (savedProvider), not the selected
          tile: uploads embed against the active provider, so availability + the button label must
          reflect what's actually in effect, updating only once a switch is saved. */}
      <KnowledgeDocumentsCard provider={savedProvider} refreshKey={reembedRefresh} uploadBlockedUntil={uploadBlockedUntil ?? undefined} />

      {saved || error ? (
        <div className="fixed bottom-4 left-1/2 z-[900] w-[36rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 sm:bottom-8">
          <div className="smycles-toast">
            {/* min-h matches the SaveBar's button-row height so a text-only
                toast is the same pill height as the profile SaveBar. */}
            <span className="flex min-h-6 min-w-0 flex-1 items-center">
              {error ??
                intl.formatMessage({
                  id: "model.saved",
                  defaultMessage: "Settings saved – may take up to 30 seconds to take effect.",
                })}
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
}
